import * as Sentry from '@sentry/react';

/**
 * Matches the suffix `generateErrorId()` appends to a thrown message.
 *
 * The id stays in the message on purpose — it is what the error screen shows
 * the user to quote to support. It must NOT reach Sentry's grouping, though:
 * a fresh uuid per throw is what split one failure into four issues
 * (PRO-STUDIO-41, 5T, 4H, 4Z), each titled with the id of whichever event
 * happened to open it.
 */
const ERROR_ID_SUFFIX =
  /\s*\(Error ID:\s*([0-9a-fA-F-]+)\s*please pass this ID to the support service\)\s*$/;

export interface SplitMessage {
  /** The message with the support-id suffix removed. */
  message: string;
  /** The uuid that suffix carried, when it had one. */
  errorId?: string;
}

export const splitErrorId = (raw: string): SplitMessage => {
  const match = raw.match(ERROR_ID_SUFFIX);

  if (!match) {
    return { message: raw, errorId: undefined };
  }

  return { message: raw.replace(ERROR_ID_SUFFIX, ''), errorId: match[1] };
};

export interface ReportErrorOptions {
  /** Where the report was raised — an error boundary, a machine, a hook. */
  source: string;
  /** A coarse kind, kept from the previous `extra.type`. */
  type?: string;
  tags?: Record<string, string>;
  /** Anything genuinely non-indexable. Platform identity belongs in tags. */
  extra?: Record<string, unknown>;
}

/**
 * The one place this monorepo hands an error to Sentry.
 *
 * Two things it does that a bare `captureException` does not: it groups by the
 * message with the per-throw support id stripped out, and it puts that id on a
 * searchable tag instead. The minified `stack` the previous call sites also
 * copied into `extra` is deliberately not carried — Sentry already resolves the
 * real one through the uploaded sourcemaps.
 */
export const reportError = (
  error: unknown,
  { source, type, tags, extra }: ReportErrorOptions
) => {
  const raw = error instanceof Error ? error.message : String(error);
  const { message, errorId } = splitErrorId(raw);
  const name = error instanceof Error ? error.name : 'UnknownError';

  Sentry.captureException(error, {
    fingerprint: [name, message],
    tags: {
      ...tags,
      ...(errorId ? { errorId } : {}),
    },
    extra: {
      source,
      ...(type ? { type } : {}),
      ...extra,
    },
  });
};

export interface UserIdentity {
  id?: string;
  email?: string;
  /** Account-level identity — searchable, so these become TAGS, not extra. */
  customerId?: string;
  orgName?: string;
  projectId?: string;
  solutionName?: string;
}

const ACCOUNT_TAG_KEYS = [
  'customerId',
  'orgName',
  'projectId',
  'solutionName',
] as const;

/**
 * Attaches who is affected, and which account they are in, to every later event.
 *
 * `null` clears it, which matters on sign-out: leaving the previous identity
 * attached would credit the next person's errors to them.
 */
export const identifyUser = (identity: UserIdentity | null) => {
  if (!identity) {
    Sentry.setUser(null);

    return;
  }

  Sentry.setUser({ id: identity.id, email: identity.email });

  const tags: Record<string, string> = {};

  ACCOUNT_TAG_KEYS.forEach((key) => {
    const value = identity[key];

    // An absent value is omitted rather than sent: Sentry would store the
    // string "undefined" and it would then appear as a real, filterable value.
    if (value) {
      tags[key] = value;
    }
  });

  Sentry.setTags(tags);
};
